import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { cancelCustomDashboardDataUpdate } from '@/lib/api/custom-dashboard';
import { allTabsQueryKey } from '../allTabsQueryKey';
import type { CancelRequestTarget, DataUpdateRequestTarget } from '../dataUpdateTargets';

export interface UseDataUpdateDialogsResult {
  dataUpdateTarget: DataUpdateRequestTarget | null;
  cancelTarget: CancelRequestTarget | null;
  isCancelling: boolean;
  requestDataUpdate: (target: DataUpdateRequestTarget) => void;
  closeDataUpdate: () => void;
  requestCancel: (target: CancelRequestTarget) => void;
  closeCancel: () => void;
  confirmCancel: () => Promise<void>;
}

/**
 * データ更新の実行ダイアログ・キャンセル確認ダイアログの state とキャンセル API 呼び出しをまとめる。
 * `TabConfigContent`（タブの並びの変化で remount される）の外、`TabConfigScreen` 側で使うことで、
 * ポーリング中の他ユーザーのタブの追加・削除・並べ替えでもダイアログが無言で閉じないようにする。
 */
export function useDataUpdateDialogs(
  accountId: string,
  authHeaders: Record<string, string>,
): UseDataUpdateDialogsResult {
  const queryClient = useQueryClient();
  const [dataUpdateTarget, setDataUpdateTarget] = useState<DataUpdateRequestTarget | null>(null);
  const [cancelTarget, setCancelTarget] = useState<CancelRequestTarget | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);

  const confirmCancel = async () => {
    if (cancelTarget === null) return;
    setIsCancelling(true);
    try {
      await cancelCustomDashboardDataUpdate(
        accountId,
        cancelTarget.tabId,
        cancelTarget.jobId,
        authHeaders,
      );
      toast.success('データ更新ジョブをキャンセルしました');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'キャンセルに失敗しました');
    } finally {
      // 既に終端状態だった場合の 409 でも、次のポーリングを待たずに行の表示を揃える。
      await queryClient.invalidateQueries({ queryKey: allTabsQueryKey(accountId) });
      setIsCancelling(false);
      setCancelTarget(null);
    }
  };

  return {
    dataUpdateTarget,
    cancelTarget,
    isCancelling,
    requestDataUpdate: setDataUpdateTarget,
    closeDataUpdate: () => setDataUpdateTarget(null),
    requestCancel: setCancelTarget,
    closeCancel: () => setCancelTarget(null),
    confirmCancel,
  };
}
